import { useState, useEffect, useCallback } from "react";
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from "react-joyride";
import { useRole } from "@/contexts/RoleContext";

const TOUR_KEY = "aiia-guided-tour-completed";

interface GuidedTourProps {
  run?: boolean;
  onComplete?: () => void;
}

export function GuidedTour({ run: externalRun, onComplete }: GuidedTourProps) {
  const { isPartner, isClient, isSolo } = useRole();
  const [run, setRun] = useState(false);

  // Start tour when externally triggered
  useEffect(() => {
    if (externalRun) {
      setRun(true);
    }
  }, [externalRun]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, type } = data;
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      if (finishedStatuses.includes(status)) {
        setRun(false);
        localStorage.setItem(TOUR_KEY, "true");
        onComplete?.();
      }

      // Close tour on escape or overlay click
      if (action === ACTIONS.CLOSE || (type === EVENTS.TARGET_NOT_FOUND && action === ACTIONS.UPDATE)) {
        setRun(false);
      }
    },
    [onComplete]
  );

  // Role-specific tour steps
  const getSteps = (): Step[] => {
    if (isPartner) {
      return [
        {
          target: '[data-tour="dashboard"]',
          content:
            "Welcome to your Partner Dashboard! Here you can see an overview of all your clients and their instances.",
          disableBeacon: true,
          placement: "bottom",
        },
        {
          target: '[data-tour="client-switcher"]',
          content:
            "Use the Client Switcher to quickly jump between your different client organizations. You can also use Cmd+K (or Ctrl+K on Windows) to open it.",
          placement: "bottom",
        },
        {
          target: '[data-tour="billing"]',
          content:
            "View and manage billing information for all your clients in one place.",
          placement: "right",
        },
        {
          target: '[data-tour="installation-indicator"]',
          content:
            "This indicator shows when installations are in progress. Click it to see details and track progress in real-time.",
          placement: "bottom",
        },
      ];
    }

    if (isClient) {
      return [
        {
          target: '[data-tour="dashboard"]',
          content:
            "Welcome to your Dashboard! Here you can see an overview of your Odoo instances and installed BoBs.",
          disableBeacon: true,
          placement: "bottom",
        },
        {
          target: '[data-tour="instances"]',
          content:
            "Manage all your Odoo instances here. You can create new instances, configure existing ones, and install BoBs.",
          placement: "right",
        },
        {
          target: '[data-tour="subscription"]',
          content:
            "View your subscription details and manage your billing preferences.",
          placement: "right",
        },
        {
          target: '[data-tour="users"]',
          content:
            "Invite team members and manage their access permissions to your organization.",
          placement: "right",
        },
        {
          target: '[data-tour="installation-indicator"]',
          content:
            "This indicator shows when installations are in progress. Click it to see details and track progress in real-time.",
          placement: "bottom",
        },
      ];
    }

    // Solo user steps
    return [
      {
        target: '[data-tour="dashboard"]',
        content:
          "Welcome to your Dashboard! Here you can see your trial status and quick actions to get started.",
        disableBeacon: true,
        placement: "bottom",
      },
      {
        target: '[data-tour="instances"]',
        content:
          "Create and manage your Odoo instances here. Click 'Create Instance' to set up your first instance.",
        placement: "right",
      },
      {
        target: '[data-tour="billing"]',
        content:
          "Manage your prepaid credits and view transaction history. You're currently on a 14-day free trial!",
        placement: "right",
      },
      {
        target: '[data-tour="installation-indicator"]',
        content:
          "This indicator shows when installations are in progress. Click it to see details.",
        placement: "bottom",
      },
    ];
  };

  const steps = getSteps();

  // Custom styles to match our dark theme
  const joyrideStyles = {
    options: {
      arrowColor: "hsl(0 0% 10%)",
      backgroundColor: "hsl(0 0% 10%)",
      overlayColor: "rgba(0, 0, 0, 0.7)",
      primaryColor: "hsl(221 83% 53%)", // accent color
      textColor: "hsl(30 17% 96%)", // foreground
      spotlightShadow: "0 0 15px rgba(59, 130, 246, 0.5)",
      zIndex: 10000,
    },
    tooltipContainer: {
      textAlign: "left" as const,
    },
    tooltipTitle: {
      fontSize: "16px",
      fontWeight: 600,
    },
    tooltipContent: {
      fontSize: "14px",
      padding: "10px 0",
    },
    buttonNext: {
      backgroundColor: "hsl(221 83% 53%)",
      borderRadius: "6px",
      fontSize: "14px",
      padding: "8px 16px",
    },
    buttonBack: {
      color: "hsl(0 0% 64%)",
      fontSize: "14px",
    },
    buttonSkip: {
      color: "hsl(0 0% 64%)",
      fontSize: "14px",
    },
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      spotlightClicks
      disableScrolling={false}
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
      locale={{
        back: "Previous",
        close: "Close",
        last: "Finish",
        next: "Next",
        open: "Open the dialog",
        skip: "Skip tour",
      }}
      floaterProps={{
        styles: {
          floater: {
            filter: "drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3))",
          },
        },
      }}
    />
  );
}

/**
 * Reset the guided tour so it can be shown again.
 * Useful for testing or when user wants to see the tour again.
 */
export function resetGuidedTour() {
  localStorage.removeItem(TOUR_KEY);
}

/**
 * Check if the guided tour has been completed.
 */
export function hasCompletedTour(): boolean {
  return localStorage.getItem(TOUR_KEY) === "true";
}
